import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LuPresentation,
  LuHeadphones,
  LuBookOpenCheck,
  LuEye,
  LuUsers,
  LuMapPin,
  LuChevronRight,
  LuArrowLeft,
  LuArrowRight,
  LuX,
} from "react-icons/lu";
import StarIcon from "../ui/StarIcon";
import * as styled from "./PlatformLessonsTour.styled";
import { fetchAllPlatformLessons } from "../../helpers/materials/platformLessons";

// Одноразовий тур для вчителя: нові уроки платформи — що це, для кого, де знайти, як перевести учня.
// Повторно відкривається з блоку «Новинка» на головній (подія OPEN_EVENT).
export const OPEN_EVENT = "open-platform-lessons-tour";
const storageKey = (teacherId) => `platformLessonsTourSeen:${teacherId}`;

const GROUPS = { adults: "Дорослі", teenagers: "Підлітки", kids: "Діти" };
const courseName = (course) => {
  const [group, ...level] = course.split("-");
  const levelName = level.join("-") === "trial" ? "Пробний урок" : level.join("-").replace(/\b\w/g, (letter) => letter.toUpperCase());
  return `${GROUPS[group] || group} · ${levelName}`;
};

const wasSeen = (teacherId) => {
  try {
    return window.localStorage.getItem(storageKey(teacherId)) === "1";
  } catch (error) {
    return true; // сховище недоступне — не набридаємо вікном щоразу
  }
};

function PlatformLessonsTour({ teacher }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [courses, setCourses] = useState([]);

  // Уроки платформи поки лише англійські; тур — лише якщо курси справді є
  useEffect(() => {
    if (teacher?.accessType !== "en") return;
    fetchAllPlatformLessons()
      .then((lessons) => {
        const counts = {};
        // Лише англійські курси: інші мови мають префікс ("es-kids-a1")
        lessons.forEach(({ course }) => !/^[a-z]{2}-/.test(course) && (counts[course] = (counts[course] || 0) + 1));
        const list = Object.entries(counts).map(([course, count]) => ({ course, count }));
        setCourses(list);
        if (list.length && !wasSeen(teacher._id)) setOpen(true);
      })
      .catch(() => {});
  }, [teacher?._id, teacher?.accessType]);

  useEffect(() => {
    const reopen = () => {
      setStep(0);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, reopen);
    return () => window.removeEventListener(OPEN_EVENT, reopen);
  }, []);

  const close = () => {
    try {
      window.localStorage.setItem(storageKey(teacher._id), "1");
    } catch (error) {}
    setOpen(false);
  };

  const openLessons = () => {
    try {
      window.localStorage.setItem("materialsMode", "platform");
    } catch (error) {}
    close();
    navigate("/teacher/materials");
  };

  if (!open || !courses.length) return null;

  const steps = [
    {
      label: "Нове на платформі",
      title: "Уроки тепер прямо на платформі",
      body: (
        <>
          <styled.Lead>Замість Canva — інтерактивні уроки-презентації. Усе для уроку в одному місці.</styled.Lead>
          {[
            [LuPresentation, "Слайди з вправами: учень відповідає — одразу видно, правильно чи ні"],
            [LuHeadphones, "Аудіо й відео вбудовані у слайди — нічого не треба шукати"],
            [LuEye, "Для вас — відповіді та нотатки з методикою до кожного слайда"],
            [LuBookOpenCheck, "ДЗ учень робить у кабінеті й отримує зірку"],
          ].map(([Icon, text]) => (
            <styled.Point key={text}>
              <styled.PointIcon>
                <Icon size={18} />
              </styled.PointIcon>
              {text}
            </styled.Point>
          ))}
        </>
      ),
    },
    {
      label: "Для кого",
      title: "Для кого вже є уроки",
      body: (
        <>
          {courses.map(({ course, count }) => (
            <styled.Course key={course}>
              <b>{courseName(course)}</b>
              {!course.endsWith("-trial") && <span>{count} уроків</span>}
            </styled.Course>
          ))}
          <styled.Note>
            Інші групи й рівні поки працюють на Canva — нові уроки додаємо поступово, і вони зʼявляться тут самі.
          </styled.Note>
        </>
      ),
    },
    {
      label: "Де знайти",
      title: "Де знайти й як вести урок",
      body: (
        <>
          <styled.Path>
            {["Матеріали", "Уроки платформи", "Дорослі", "Beginner"].map((item, index) => (
              <span key={item}>
                {index > 0 && <LuChevronRight size={14} />}
                <styled.Crumb $last={index === 3}>{item}</styled.Crumb>
              </span>
            ))}
          </styled.Path>
          <styled.Lead>Відкрийте урок — це презентація. Покажіть екран учню в Google Meet.</styled.Lead>
          <styled.Keys>
            {[
              ["← →", "слайди"],
              ["F", "на весь екран"],
              ["A", "показати відповіді"],
              ["T", "нотатка для вчителя"],
              ["G", "усі слайди"],
            ].map(([key, text]) => (
              <styled.Key key={key}>
                <kbd>{key}</kbd>
                {text}
              </styled.Key>
            ))}
          </styled.Keys>
        </>
      ),
    },
    {
      label: "Учні та ДЗ",
      title: "Як перевести учня на нові уроки",
      body: (
        <>
          <styled.Mock>
            <span>Матеріали:</span>
            <styled.MockSegment $active>Уроки платформи</styled.MockSegment>
            <styled.MockSegment>Canva</styled.MockSegment>
          </styled.Mock>
          {[
            [LuUsers, "«Мої учні» → у картці учня перемикач «Матеріали». Переводьте, коли зручно — наприклад, з нового юніту"],
            [LuMapPin, "Нові учні цих рівнів одразу додаються на платформу"],
            [LuBookOpenCheck, "Відмічаєте урок платформи — учню автоматично приходить інтерактивне ДЗ"],
          ].map(([Icon, text]) => (
            <styled.Point key={text}>
              <styled.PointIcon>
                <Icon size={18} />
              </styled.PointIcon>
              {text}
            </styled.Point>
          ))}
        </>
      ),
    },
  ];

  const last = step === steps.length - 1;
  const current = steps[step];
  const HeroIcon = [LuPresentation, LuUsers, LuMapPin][step] || LuPresentation;

  return (
    <styled.Overlay onClick={close}>
      <styled.Modal role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <styled.Close type="button" aria-label="Закрити" onClick={close}>
          <LuX size={20} />
        </styled.Close>
        <styled.Hero>
          <styled.HeroIcon key={step}>{step === 3 ? <StarIcon size={44} /> : <HeroIcon size={40} />}</styled.HeroIcon>
          <styled.HeroLabel>
            {current.label} · {step + 1}/{steps.length}
          </styled.HeroLabel>
          <styled.Title>{current.title}</styled.Title>
        </styled.Hero>
        <styled.Body key={step}>{current.body}</styled.Body>
        <styled.Footer>
          <styled.Dots>
            {steps.map((_, index) => (
              <styled.Dot key={index} type="button" $active={index === step} aria-label={`Крок ${index + 1}`} onClick={() => setStep(index)} />
            ))}
          </styled.Dots>
          <styled.Buttons>
            {step > 0 && (
              <styled.Secondary type="button" onClick={() => setStep(step - 1)}>
                <LuArrowLeft size={18} />
              </styled.Secondary>
            )}
            {last ? (
              <styled.Primary type="button" onClick={openLessons}>
                Відкрити уроки
              </styled.Primary>
            ) : (
              <styled.Primary type="button" onClick={() => setStep(step + 1)}>
                Далі <LuArrowRight size={18} />
              </styled.Primary>
            )}
          </styled.Buttons>
        </styled.Footer>
      </styled.Modal>
    </styled.Overlay>
  );
}

export default PlatformLessonsTour;
